import React, { useEffect, useState } from "react";
import { ambilAlasanKeluar, IDLE_MENIT } from "../lib/useIdleLogout";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, RefreshCw, ShieldCheck, Loader2, Store, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import api, { formatApiError } from "../lib/api";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState({ captcha_id: "", question: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Pesan bila pengguna terlempar keluar karena sesi diam terlalu lama.
  const [pesanSesi, setPesanSesi] = useState("");

  const loadCaptcha = async () => {
    try {
      const { data } = await api.get("/auth/captcha");
      setCaptcha(data);
      setCaptchaAnswer("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { loadCaptcha(); }, []);

  useEffect(() => {
    if (ambilAlasanKeluar() === "idle") {
      setPesanSesi(
        `Sesi Anda berakhir otomatis karena tidak ada aktivitas selama ${IDLE_MENIT} menit. Silakan masuk kembali.`
      );
    }
  }, []);
  useEffect(() => {
    if (ready && user && user.id) navigate("/dashboard", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login({
        email,
        password,
        captcha_id: captcha.captcha_id,
        captcha_answer: captchaAnswer,
      });
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      await loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left: branding panel */}
      <div className="hidden lg:flex lg:w-7/12 login-bg text-white p-12 flex-col justify-between relative">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <div className="text-sm tracking-[0.2em] uppercase opacity-80">Pemerintah Provinsi Sumatera Utara</div>
            <div className="text-xl font-display font-semibold">KEDAN MBG</div>
          </div>
        </div>

        <div className="max-w-xl">
          <div className="kedan-label text-white/70 mb-3">Sistem Monitoring</div>
          <h1 className="text-4xl xl:text-5xl font-display font-bold leading-tight">
            Kebutuhan & Ketersediaan Pangan Sumatera Utara.
          </h1>
          <p className="mt-5 text-white/85 text-lg leading-relaxed">
            Monitoring rantai pasok pangan secara real-time,
            persediaan komoditas, hingga distribusi di seluruh Sumatera Utara.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-6">
            {[
              { v: "24+", l: "Kabupaten/Kota" },
              { v: "100%", l: "Terverifikasi" },
              { v: "100+", l: "Pemantauan Supplier" },
            ].map((s, i) => (
              <div key={i} className="border-l-2 border-white/30 pl-4">
                <div className="text-2xl font-display font-bold">{s.v}</div>
                <div className="text-xs uppercase tracking-widest text-white/70 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-white/60 uppercase tracking-widest">
          © {new Date().getFullYear()} KEDAN MBG · Pemerintah Provinsi Sumatera Utara
        </div>
      </div>

      {/* Right: form + support image */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Form area */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-md bg-[#045498] flex items-center justify-center text-white">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="text-xl font-display font-bold text-slate-900">KEDAN MBG</div>
            </div>

            <div className="kedan-label mb-2">Portal Resmi</div>
            <h2 className="text-3xl font-display font-bold text-slate-900">Masuk ke Dashboard</h2>
            <p className="mt-2 text-slate-500 text-sm">
              Gunakan akun yang telah diberikan oleh Admin.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="kedan-input" placeholder="nama@kedanmbg.id"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="kedan-input pr-10" placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Verifikasi Captcha</label>
                <div className="flex gap-2 items-stretch">
                  <div className="flex-1 bg-slate-100 border border-slate-200 rounded-md px-4 py-2 flex items-center justify-center font-mono text-base font-semibold text-slate-800 select-none whitespace-nowrap min-h-[42px]"
                    data-testid="login-captcha-question">
                    {captcha.question || "..."}
                  </div>
                  <input
                    type="text" required value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)}
                    className="kedan-input w-24 text-center font-mono"
                    placeholder="?" data-testid="login-captcha-input"
                  />
                  <button
                    type="button" onClick={loadCaptcha}
                    className="kedan-btn-secondary !px-3" title="Refresh captcha"
                    data-testid="login-captcha-refresh"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {pesanSesi && !error && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md px-3 py-2"
                  data-testid="login-idle-notice">
                  {pesanSesi}
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2"
                  data-testid="login-error">
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading}
                className="kedan-btn-primary w-full !py-2.5"
                data-testid="login-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                {loading ? "Memverifikasi..." : "Masuk"}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="text-sm text-slate-600 text-center">
                Belum punya akun supplier?
              </div>
              <Link
                to="/daftar-supplier"
                className="kedan-btn-secondary w-full justify-center mt-3"
                data-testid="login-register-supplier-link"
              >
                <Store className="w-4 h-4" /> Daftar sebagai Supplier
              </Link>
              <p className="text-xs text-slate-400 text-center mt-2">
                Akun baru berstatus belum terverifikasi hingga disetujui KPPG/Pemda.
              </p>
            </div>
          </div>
        </div>

        {/* Support image — desktop: bottom of right panel, mobile: below form */}
        <div className="login-support-image-container">
          <img
            src="/support_kedanmbg_2026.png"
            alt="Didukung oleh KEDAN MBG 2026"
            className="login-support-image"
          />
        </div>
      </div>
    </div>
  );
}
